import { useState } from 'react';
import './LoginModal.css';
import { IoMdEye } from "react-icons/io";
import { FaEyeSlash } from "react-icons/fa";

export const LoginModal = ({ 
  isOpen, 
  onClose, 
  onLogin, 
  onConfirmEmail,
  onCancelConfirmation,
  onResetPassword,
  onConfirmResetPassword,
  onSetNewPassword,
  onCancelResetPassword,
  onStartResetPasswordFlow,
  isLoading, 
  error,
  needsConfirmation,
  pendingEmail,
  resetStage, // 'email' | 'code' | 'password' | null
  resetEmail,
}) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState('');
  
  // Состояния для сброса пароля
  const [resetPasswordEmail, setResetPasswordEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [resetCodeError, setResetCodeError] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  const [newPasswordError, setNewPasswordError] = useState('');

  if (!isOpen) return null;

  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    try {
      await onLogin(email, password);
    } catch {
      // Ошибка обрабатывается в useAuth
    }
  };

  const handleCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setCode(value);
    setCodeError('');
  };

  const handleConfirmSubmit = async (e) => {
    e.preventDefault();

    if (code.length !== 6) {
      setCodeError('Введите 6-значный код');
      return;
    }

    try {
      await onConfirmEmail(code, email);
      setCode('');
    } catch {
      // Ошибка обрабатывается в useAuth
    }
  };

  const handleClose = () => {
    setEmail('');
    setPassword('');
    setShowPassword(false);
    setCode('');
    setCodeError('');
    setResetPasswordEmail('');
    setResetCode('');
    setResetCodeError('');
    setNewPassword('');
    setShowNewPassword(false);
    setConfirmNewPassword('');
    setShowConfirmNewPassword(false);
    setNewPasswordError('');
    if (needsConfirmation) {
      onCancelConfirmation();
    }
    onClose();
  };

  // Обработчики для сброса пароля
  const handleResetPasswordSubmit = async (e) => {
    e.preventDefault();
    try {
      await onResetPassword(resetPasswordEmail);
    } catch {
      // Ошибка обрабатывается в useAuth
    }
  };

  const handleResetCodeChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setResetCode(value);
    setResetCodeError('');
  };

  const handleConfirmResetCodeSubmit = async (e) => {
    e.preventDefault();

    if (resetCode.length !== 6) {
      setResetCodeError('Введите 6-значный код');
      return;
    }

    try {
      await onConfirmResetPassword(resetCode, resetPasswordEmail);
    } catch {
      // Ошибка обрабатывается в useAuth
    }
  };

  const handleSetNewPasswordSubmit = async (e) => {
    e.preventDefault();

    if (newPassword.length < 6) {
      setNewPasswordError('Пароль должен содержать минимум 6 символов');
      return;
    }

    if (newPassword !== confirmNewPassword) {
      setNewPasswordError('Пароли не совпадают');
      return;
    }

    try {
      await onSetNewPassword(newPassword);
      setNewPassword('');
      setConfirmNewPassword('');
    } catch {
      // Ошибка обрабатывается в useAuth
    }
  };

  // Экран сброса пароля - шаг 1 (ввод email)
  if (resetStage === 'email') {
    return (
      <div className="modal-overlay" onClick={handleClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Сброс пароля</h2>
            <button className="modal-close" onClick={handleClose}>×</button>
          </div>
          <form onSubmit={handleResetPasswordSubmit}>
            {error && <div className="modal-error">{error}</div>}
            <div className="confirmation-info">
              <p>Введите email, привязанный к вашему аккаунту.</p>
              <p>Мы отправим на него код для сброса пароля.</p>
            </div>
            <div className="form-group">
              <label htmlFor="reset-email">Email</label>
              <input
                id="reset-email"
                type="email"
                value={resetPasswordEmail}
                onChange={(e) => setResetPasswordEmail(e.target.value)}
                placeholder="your@email.com"
                required
                autoFocus
              />
            </div>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isLoading}
            >
              {isLoading ? 'Отправка...' : 'Отправить код'}
            </button>
            <button
              type="button"
              className="btn btn-secondary cancel-btn"
              onClick={() => {
                onCancelConfirmation?.();
                setResetPasswordEmail('');
              }}
              disabled={isLoading}
            >
              Вернуться ко входу
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Экран сброса пароля - шаг 2 (ввод кода)
  if (resetStage === 'code') {
    return (
      <div className="modal-overlay" onClick={handleClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Подтверждение кода</h2>
            <button className="modal-close" onClick={handleClose}>×</button>
          </div>
          <form onSubmit={handleConfirmResetCodeSubmit}>
            {error && <div className="modal-error">{error}</div>}
            <div className="confirmation-info">
              <p>
                Мы отправили 6-значный код подтверждения на адрес{' '}
                <strong>{resetEmail || resetPasswordEmail}</strong>
              </p>
              <p>Введите код ниже, чтобы продолжить сброс пароля.</p>
            </div>
            <div className="form-group">
              <label htmlFor="reset-code">Код подтверждения</label>
              <input
                id="reset-code"
                type="text"
                inputMode="numeric"
                value={resetCode}
                onChange={handleResetCodeChange}
                placeholder="000000"
                maxLength={6}
                autoFocus
                required
                className={resetCodeError ? 'input-error' : ''}
              />
              {resetCodeError && <span className="field-error">{resetCodeError}</span>}
            </div>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isLoading || resetCode.length !== 6}
            >
              {isLoading ? 'Проверка...' : 'Подтвердить'}
            </button>
            <button
              type="button"
              className="btn btn-secondary cancel-btn"
              onClick={() => {
                onCancelConfirmation?.();
                setResetCode('');
              }}
              disabled={isLoading}
            >
              Вернуться
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Экран сброса пароля - шаг 3 (установка нового пароля)
  if (resetStage === 'password') {
    return (
      <div className="modal-overlay" onClick={handleClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Новый пароль</h2>
            <button className="modal-close" onClick={handleClose}>×</button>
          </div>
          <form onSubmit={handleSetNewPasswordSubmit}>
            {error && <div className="modal-error">{error}</div>}
            <div className="form-group password-field">
              <label htmlFor="new-password">Новый пароль</label>
              <div className="password-input-wrapper">
                <input
                  id="new-password"
                  type={showNewPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => {
                    setNewPassword(e.target.value);
                    setNewPasswordError('');
                  }}
                  placeholder="••••••••"
                  required
                  autoFocus
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  aria-label={showNewPassword ? 'Скрыть пароль' : 'Показать пароль'}
                >
                  {showNewPassword ? <FaEyeSlash  /> : <IoMdEye />}
                </button>
              </div>
            </div>
            <div className="form-group password-field">
              <label htmlFor="confirm-new-password">Подтвердить пароль</label>
              <div className="password-input-wrapper">
                <input
                  id="confirm-new-password"
                  type={showConfirmNewPassword ? 'text' : 'password'}
                  value={confirmNewPassword}
                  onChange={(e) => {
                    setConfirmNewPassword(e.target.value);
                    setNewPasswordError('');
                  }}
                  placeholder="••••••••"
                  required
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowConfirmNewPassword(!showConfirmNewPassword)}
                  aria-label={showConfirmNewPassword ? 'Скрыть пароль' : 'Показать пароль'}
                >
                  {showConfirmNewPassword ? <FaEyeSlash  /> : <IoMdEye />}
                </button>
              </div>
            </div>
            {newPasswordError && (
              <div className="field-error" style={{ marginBottom: '10px' }}>
                {newPasswordError}
              </div>
            )}
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isLoading || !newPassword || !confirmNewPassword}
            >
              {isLoading ? 'Сохранение...' : 'Сохранить пароль'}
            </button>
            <button
              type="button"
              className="btn btn-secondary cancel-btn"
              onClick={handleClose}
              disabled={isLoading}
            >
              Отмена
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Экран подтверждения email
  if (needsConfirmation) {
    return (
      <div className="modal-overlay" onClick={handleClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header">
            <h2>Подтверждение входа</h2>
            <button className="modal-close" onClick={handleClose}>×</button>
          </div>
          <form onSubmit={handleConfirmSubmit}>
            {error && <div className="modal-error">{error}</div>}
            <div className="confirmation-info">
              <p>
                Мы отправили 6-значный код подтверждения на адрес{' '}
                <strong>{pendingEmail}</strong>
              </p>
              <p>Введите код ниже, чтобы завершить вход.</p>
            </div>
            <div className="form-group">
              <label htmlFor="code">Код подтверждения</label>
              <input
                id="code"
                type="text"
                inputMode="numeric"
                value={code}
                onChange={handleCodeChange}
                placeholder="000000"
                maxLength={6}
                autoFocus
                required
                className={codeError ? 'input-error' : ''}
              />
              {codeError && <span className="field-error">{codeError}</span>}
            </div>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isLoading || code.length !== 6}
            >
              {isLoading ? 'Проверка...' : 'Подтвердить'}
            </button>
            <button
              type="button"
              className="btn btn-secondary cancel-btn"
              onClick={onCancelConfirmation}
              disabled={isLoading}
            >
              Вернуться к входу
            </button>
          </form>
        </div>
      </div>
    );
  }

  // Экран входа
  return (
    <div className="modal-overlay" onClick={handleClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Вход в аккаунт</h2>
          <button className="modal-close" onClick={handleClose}>×</button>
        </div>
        <form onSubmit={handleLoginSubmit}>
          {error && <div className="modal-error">{error}</div>}
          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
              autoFocus
            />
          </div>
          <div className="form-group password-field">
            <label htmlFor="password">Пароль</label>
            <div className="password-input-wrapper">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'Скрыть пароль' : 'Показать пароль'}
              >
                {showPassword ? <FaEyeSlash  /> : <IoMdEye />}
              </button>
            </div>
          </div>
          <div style={{ textAlign: 'left', marginBottom: '16px' }}>
            <button
              type="button"
              onClick={() => {
                onCancelConfirmation?.();
                onStartResetPasswordFlow?.();
              }}
              style={{
                background: 'none',
                border: 'none',
                color: '#ff2600',
                fontSize: '12px',
                cursor: 'pointer',
                padding: 0,
              }}
            >
              Забыли пароль?
            </button>
          </div>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={isLoading}
          >
            {isLoading ? 'Загрузка...' : 'Войти'}
          </button>
        </form>
      </div>
    </div>
  );
};